import { useState, useEffect, useCallback } from 'react';
import { extractFieldErrors } from '@/utils/formErrors';

interface UseFormStateOptions<T> {
  /** Initial form data values */
  defaultValues: T;
  /** Current item being edited (if any) */
  currentItem?: T | null;
  /** Whether the form is open/visible */
  isOpen: boolean;
  /** API error to extract field errors from */
  error?: Error | null;
  /** Transform function to convert current item to form data */
  itemToFormData?: (item: T) => T;
}

interface UseFormStateReturn<T> {
  /** Current form data */
  formData: T;
  /** Field-level validation errors */
  errors: Record<string, string>;
  /** Update a single field and clear its error */
  handleChange: (field: keyof T, value: unknown) => void;
  /** Update multiple fields at once */
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  /** Set multiple field errors at once */
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  /** Reset form to default values */
  reset: () => void;
  /** Whether we're editing an existing item */
  isEditing: boolean;
}

/**
 * Reusable form state management hook
 *
 * Handles common form patterns:
 * - Form data state management
 * - Field-level validation errors
 * - Automatic error clearing on field change
 * - Resetting form when opening/closing
 * - Extracting field errors from API errors
 *
 * @example
 * ```tsx
 * const { formData, errors, handleChange, reset } = useFormState({
 *   defaultValues: { name: '', email: '' },
 *   currentItem: user,
 *   isOpen: modalOpen,
 *   error: apiError,
 *   itemToFormData: (user) => ({
 *     name: user.name,
 *     email: user.email
 *   })
 * });
 * ```
 */
export function useFormState<T extends Record<string, any>>({
  defaultValues,
  currentItem,
  isOpen,
  error,
  itemToFormData,
}: UseFormStateOptions<T>): UseFormStateReturn<T> {
  const [formData, setFormData] = useState<T>(defaultValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!currentItem;

  // Reset form when opening/closing or current item changes
  useEffect(() => {
    if (isOpen) {
      if (currentItem && itemToFormData) {
        setFormData(itemToFormData(currentItem));
      } else if (currentItem) {
        setFormData(currentItem);
      } else {
        setFormData(defaultValues);
      }
      setErrors({});
    }
  }, [isOpen, currentItem, defaultValues, itemToFormData]);

  // Extract field errors from API error
  useEffect(() => {
    if (error) {
      const fieldErrors = extractFieldErrors(error);
      setErrors(fieldErrors);
    } else {
      setErrors({});
    }
  }, [error]);

  // Update a single field and clear its error
  const handleChange = useCallback((field: keyof T, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field when user makes a change
    setErrors((prev) => {
      if (prev[field as string]) {
        const next = { ...prev };
        delete next[field as string];
        return next;
      }
      return prev;
    });
  }, []);

  // Reset form to default values
  const reset = useCallback(() => {
    setFormData(defaultValues);
    setErrors({});
  }, [defaultValues]);

  return {
    formData,
    errors,
    handleChange,
    setFormData,
    setErrors,
    reset,
    isEditing,
  };
}
